import { PROTOCOL_VERSION } from "@legacy-manager/shared";

/**
 * Cliente WebSocket do modo online.
 *
 * O socket é um acelerador, não a única fonte: tudo que chega por ele também
 * pode ser lido pela API. Essa decisão é o que permite tratar queda de conexão
 * como incômodo em vez de catástrofe.
 *
 * Três coisas moram aqui:
 *
 * **Reconexão com espera crescente**, para não martelar um servidor que caiu.
 *
 * **Retomada por `since`**: cada canal lembra a última sequência recebida e
 * pede de onde parou. Quando o servidor responde `resync`, o pedido era velho
 * demais e a tela recarrega pela API.
 *
 * **Buraco de sequência é erro**, não detalhe. Se chegar `seq` fora de ordem,
 * o canal pede ressincronização em vez de mostrar um estado com furo.
 */

export type Ouvinte = (tipo: string, carga: unknown, canal: string | null) => void;

export interface OpcoesDoSocket {
  baseUrl: string;
  /** Busca um ticket novo. Ticket é de uso único e vale poucos segundos. */
  pedirTicket: () => Promise<string>;
  aoConectar?: () => void;
  aoCair?: (motivo: string) => void;
  /** Chamado quando um canal precisa ser recarregado pela API. */
  aoPrecisarRecarregar?: (canal: string) => void;
  /** Chamado quando a versão do protocolo não bate e o jogo precisa atualizar. */
  aoExigirAtualizacao?: () => void;
}

/** Códigos de fechamento do servidor, de docs/WEBSOCKET.md 1. */
export const FECHAMENTO = {
  TICKET_INVALIDO: 4000,
  VERSAO_INCOMPATIVEL: 4001,
  CONEXOES_DEMAIS: 4002,
  BANIDO: 4003,
  MENSAGENS_DEMAIS: 4004,
} as const;

export class SocketDaLiga {
  private socket: WebSocket | null = null;
  private tentativas = 0;
  private fechandoDeProposito = false;
  /**
   * Última sequência recebida por canal.
   *
   * `undefined` quer dizer "assinei e ainda não recebi nada", que é diferente
   * de zero. Guardar zero fazia o primeiro evento de um canal com histórico
   * parecer um buraco: o cliente esperava `seq` 1, chegava 41, e ele pedia
   * recarga e reassinava do zero, para receber 42 e repetir tudo. Canal com
   * passado entrava em recarga permanente.
   */
  private readonly canais = new Map<string, number | undefined>();
  private readonly ouvintes = new Set<Ouvinte>();
  private pulso: number | null = null;

  constructor(private readonly opcoes: OpcoesDoSocket) {}

  get conectado(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  ouvir(ouvinte: Ouvinte): () => void {
    this.ouvintes.add(ouvinte);
    return () => this.ouvintes.delete(ouvinte);
  }

  async conectar(): Promise<void> {
    if (this.socket) return;
    this.fechandoDeProposito = false;

    let ticket: string;
    try {
      ticket = await this.opcoes.pedirTicket();
    } catch {
      this.agendarReconexao();
      return;
    }

    const url = new URL(`${this.opcoes.baseUrl}/v1/ws`);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.searchParams.set("ticket", ticket);
    // Deixa o servidor recusar versão incompatível no aperto de mão, em vez de
    // descobrirmos no primeiro evento com formato diferente.
    url.searchParams.set("v", String(PROTOCOL_VERSION));

    const socket = new WebSocket(url.toString());
    this.socket = socket;

    socket.onopen = () => {
      this.tentativas = 0;
      this.opcoes.aoConectar?.();
      // Reassina tudo de onde parou. Sem isto, reconectar perderia os eventos
      // que aconteceram enquanto a conexão estava caída.
      for (const [canal, marco] of this.canais) this.enviarSub(canal, marco);
    };

    socket.onmessage = (evento) => this.receber(evento.data);

    socket.onclose = (evento) => {
      this.pararPulso();
      this.socket = null;
      if (evento.code === FECHAMENTO.VERSAO_INCOMPATIVEL) {
        this.opcoes.aoExigirAtualizacao?.();
        return;
      }
      if (evento.code === FECHAMENTO.BANIDO) {
        this.opcoes.aoCair?.("banido");
        return;
      }
      if (evento.code === FECHAMENTO.MENSAGENS_DEMAIS) {
        // Reconectar na hora levaria ao mesmo fechamento. Avisa e volta pela
        // espera crescente, que já cresce a cada tentativa.
        this.opcoes.aoCair?.("mensagens demais");
        if (!this.fechandoDeProposito) this.agendarReconexao();
        return;
      }
      this.opcoes.aoCair?.(String(evento.code));
      if (!this.fechandoDeProposito) this.agendarReconexao();
    };

    socket.onerror = () => {
      // O fechamento vem logo depois e é lá que a reconexão é decidida.
    };
  }

  desconectar(): void {
    this.fechandoDeProposito = true;
    this.pararPulso();
    this.socket?.close(1000, "saindo");
    this.socket = null;
  }

  /** Assina um canal e passa a lembrar de onde parou. */
  assinar(canal: string): void {
    if (!this.canais.has(canal)) this.canais.set(canal, undefined);
    if (this.conectado) this.enviarSub(canal, this.canais.get(canal));
  }

  desassinar(canal: string): void {
    this.canais.delete(canal);
    this.enviar("unsub", { ch: canal });
  }

  enviar(tipo: string, carga: unknown): void {
    if (!this.conectado) return;
    this.socket?.send(JSON.stringify({ t: tipo, id: criarId(), p: carga }));
  }

  private enviarSub(canal: string, since: number | undefined): void {
    this.socket?.send(
      JSON.stringify({
        t: "sub",
        id: criarId(),
        p: since === undefined ? { ch: canal } : { ch: canal, since },
      }),
    );
  }

  private receber(bruto: unknown): void {
    if (typeof bruto !== "string") return;
    let mensagem: { t?: string; ch?: string; seq?: number; p?: unknown };
    try {
      mensagem = JSON.parse(bruto);
    } catch {
      return;
    }
    if (typeof mensagem.t !== "string") return;

    if (mensagem.t === "hello") {
      const carga = mensagem.p as { protocolVersion?: number; heartbeatMs?: number } | undefined;
      if (carga?.protocolVersion !== PROTOCOL_VERSION) {
        this.fechandoDeProposito = true;
        this.socket?.close(FECHAMENTO.VERSAO_INCOMPATIVEL, "versao incompativel");
        this.opcoes.aoExigirAtualizacao?.();
        return;
      }
      this.comecarPulso(carga.heartbeatMs ?? 25000);
    }

    if (mensagem.t === "resync") {
      const canal = (mensagem.p as { ch?: string } | undefined)?.ch;
      if (canal) {
        // Volta ao estado de quem acabou de assinar: a tela recarrega pela API
        // e o próximo evento adota a sequência que vier.
        this.canais.set(canal, undefined);
        this.opcoes.aoPrecisarRecarregar?.(canal);
      }
      return;
    }

    if (mensagem.ch && typeof mensagem.seq === "number") {
      const marco = this.canais.get(mensagem.ch);

      // Primeiro evento do canal. Não há o que comparar, então adota o número
      // que chegou como ponto de partida. Um canal com meses de histórico
      // começa em 41 ou em 4100, e isso não é buraco nenhum.
      if (marco === undefined) {
        this.canais.set(mensagem.ch, mensagem.seq);
      } else if (mensagem.seq > marco + 1) {
        // Buraco de verdade: evento perdido. Recarregar pela API é melhor que
        // mostrar um estado que não existiu. Reassina do último número que
        // chegou, para o servidor mandar só o que falta.
        this.opcoes.aoPrecisarRecarregar?.(mensagem.ch);
        this.enviarSub(mensagem.ch, marco);
        return;
      } else if (mensagem.seq <= marco) {
        // Repetido. Acontece de propósito na reconexão, porque o servidor
        // assina antes de ler o histórico para não perder o que passa no meio.
        return;
      } else {
        this.canais.set(mensagem.ch, mensagem.seq);
      }
    }

    for (const ouvinte of this.ouvintes) ouvinte(mensagem.t, mensagem.p, mensagem.ch ?? null);
  }

  private comecarPulso(intervalo: number): void {
    this.pararPulso();
    this.pulso = window.setInterval(() => this.enviar("ping", {}), intervalo);
  }

  private pararPulso(): void {
    if (this.pulso !== null) {
      window.clearInterval(this.pulso);
      this.pulso = null;
    }
  }

  private agendarReconexao(): void {
    this.tentativas += 1;
    // Espera crescente com teto de trinta segundos, mais um tanto sorteado
    // para não ter mil clientes voltando no mesmo instante.
    const base = Math.min(30000, 500 * 2 ** Math.min(this.tentativas, 6));
    const espera = base + Math.random() * 500;
    window.setTimeout(() => void this.conectar(), espera);
  }
}

function criarId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}
